import { HollowedButton, NormalButton } from '@shared/designSystem/Button';
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import { Modal } from '@shared/designSystem/messages/Modal';
import { SystemIcon } from '@shared/designSystem/SystemIcon';
import { SmallText } from '@shared/designSystem/Texts';
import UserCard from '@shared/designSystem/UserCard';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { AppAdoptionTableRow } from './AppAdoptionScreenViewModel';

interface SendInvitationsModalScreenProps {
    isOpen: boolean;
    rows: AppAdoptionTableRow[];
    isSending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Confirmation before a WhatsApp send: messaging collaborators on the company's behalf
 * is outward-facing, so the admin sees exactly who will receive the install link before
 * anything leaves. Purely presentational — the view model owns the selection and the
 * send; the parent screen fires the toasts.
 */
const SendInvitationsModalScreen: React.FC<SendInvitationsModalScreenProps> = ({
    isOpen,
    rows,
    isSending,
    onCancel,
    onConfirm,
}) => {
    const confirmLabel = rows.length === 1
        ? 'Enviar 1 invitación'.localized()
        : `${'Enviar'.localized()} ${rows.length} ${'invitaciones'.localized()}`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            title={'Enviar invitaciones'.localized()}
            subtitle={'Se enviará un mensaje de WhatsApp con el link de instalación a los colaboradores seleccionados.'.localized()}
            width="760px"
            height="800px"
            maxWidth="92vw"
            maxHeight="80vh"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapMd} alignment="stretch">
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {rows.map((row) => (
                        <div
                            key={row.userID}
                            style={{
                                padding: `${designSystemStyles.paddingXsm} 0`,
                                borderBottom: `${designSystemStyles.dividerThickness} solid ${designSystemStyles.colorDividerSubtle}`,
                            }}
                        >
                            <UserCard
                                name={row.name}
                                subtitle={row.phoneLabel || 'Sin número'.localized()}
                                imageURL={row.imageURL}
                                width="100%"
                            />
                        </div>
                    ))}
                </div>
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapSm}
                    alignment="center"
                    backgroundColor={designSystemStyles.colorBackgroundGray2}
                    borderRadius={designSystemStyles.radiusItem}
                    padding={designSystemStyles.paddingSm}
                >
                    <SystemIcon type="info" color={designSystemStyles.colorInfo} />
                    <SmallText text={'Los colaboradores que ya tienen la app instalada serán omitidos.'.localized()} />
                </HorizontalContainer>
                <HorizontalContainer width="100%" justifyContent="flex-end" gap={designSystemStyles.gapSm}>
                    <HollowedButton
                        text={'Cancelar'.localized()}
                        onClick={onCancel}
                        disabled={isSending}
                    />
                    <NormalButton
                        text={confirmLabel}
                        onClick={onConfirm}
                        loading={isSending}
                        disabled={isSending || rows.length === 0}
                        width="220px"
                    />
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

export default SendInvitationsModalScreen;
